import React, { useState, useEffect } from 'react';
import { grade, gradeColor, paidTotal, uid } from '../db';
import { Avatar, Badge, Btn, Card, CardHead, Modal, ModalFooter, NoData, Search, Select, TblWrap, toast } from '../ui.jsx';
import {
  adminUploadStudentDocument, reviewStudentDocument,
  getStudentDocument, getStudentDocuments,
} from '../storage';
import { fmtDob } from '../utils/fmtDob';

function TeacherStudents({ db, save, teacher, activeSessionId }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [photo, setPhoto] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const myClasses = (teacher.cls || '').split(',').map(x => x.trim()).filter(Boolean);
  const myCls = myClasses.join(', ');
  const teacherName = `${teacher.fn || ''} ${teacher.ln || ''}`.trim() || teacher.puser || 'Teacher';

  const students = db.students.filter(s =>
    (myClasses.length === 0 || myClasses.includes(s.cls)) &&
    (!q || `${s.fn} ${s.ln} ${s.roll}`.toLowerCase().includes(q.toLowerCase()))
  );

  const sf = k => v => setForm(f => ({ ...f, [k]: v }));

  const openAdd = () => {
    setForm({ fn: '', ln: '', cls: myClasses[0] || '', gn: 'Male', fst: 'Pending', roll: '', admno: '', dob: '', father: '', fphone: '', ph: '' });
    setPhoto(null);
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (s) => {
    setForm({ ...s });
    setPhoto((db.photos || {})[s.sid || s.id] || null);
    setEditId(s.sid || s.id);
    setOpen(true);
  };

  const saveStu = () => {
    if (!form.fn?.trim()) { toast('First name required', 'err'); return; }
    if (!form.cls?.trim()) { toast('Class is required', 'err'); return; }
    if (myClasses.length > 0 && !myClasses.includes(form.cls)) { toast('You can only add students to your assigned class', 'err'); return; }
    setSaving(true);
    const id = editId || 'S' + uid();
    const s = {
      ...form, id, sid: id,
      roll: form.ro || form.roll || '',
      admno: form.an || form.admno || '',
      father: form.fa || form.father || '',
      mother: form.ma || form.mother || '',
      fphone: form.fp || form.fphone || '',
      blood: form.bl || form.blood || '',
      caste: form.ca || form.caste || '',
      aadhar: form.aa || form.aadhar || '',
      city: form.cy || form.city || '',
      fee: parseFloat(form.fe || form.fee) || 0,
      mf: parseFloat(form.mf) || 0,
      fst: form.fst || 'Pending',
      _lastModifiedBy: teacherName,
      _lastModifiedAt: new Date().toISOString(),
      _lastModifiedType: editId ? 'edit' : 'add',
    };
    const newStudents = editId
      ? db.students.map(x => (x.sid || x.id) === editId ? s : x)
      : [...db.students, s];
    const newPhotos = { ...(db.photos || {}) };
    if (photo) newPhotos[id] = photo; else if (editId) delete newPhotos[id];
    save({ ...db, students: newStudents, photos: newPhotos });
    toast(editId ? 'Student updated' : 'Student added');
    setOpen(false);
    setSaving(false);
  };

  return (
    <div className="teacher-students-page">
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1e293b', fontFamily: 'Manrope,sans-serif', margin: 0 }}>My Students</h1>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{myCls ? `Class ${myCls}` : 'All students'} · {students.length} enrolled</p>
        </div>
        <button onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 10, background: 'linear-gradient(135deg,#1960a3,#3b82f6)', color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>person_add</span> Add Student
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf5', boxShadow: '0 2px 12px rgba(0,32,69,0.06)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <span className="material-symbols-outlined" style={{ position: 'absolute', left: 10, fontSize: 16, color: '#94a3b8', pointerEvents: 'none' }}>search</span>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search students…"
              style={{ paddingLeft: 34, paddingRight: 12, paddingTop: 7, paddingBottom: 7, borderRadius: 20, border: '1.5px solid #e2e8f0', fontSize: 12, fontFamily: 'inherit', color: '#1e293b', width: 240, outline: 'none' }} />
          </div>
        </div>
        {students.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 40, display: 'block', marginBottom: 8 }}>group</span>
            <div style={{ fontSize: 13 }}>No students found</div>
          </div>
        ) : (
          <div className="teacher-table-wrap" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                {['Roll', 'Name', 'Gender', 'Father', 'Phone', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 20px', textAlign: h === 'Actions' ? 'right' : 'left', fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', background: '#f8fafc' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{students.map((s, i) => (
                <tr key={s.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                  <td style={{ padding: '12px 20px', fontSize: 12, color: '#64748b' }}>{s.roll || '—'}</td>
                  <td style={{ padding: '12px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                        {(db.photos || {})[s.sid || s.id]
                          ? <img src={(db.photos || {})[s.sid || s.id]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#1960a3,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff' }}>
                              {(s.fn || '?')[0]}{(s.ln || '')[0]}
                            </div>
                        }
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{s.fn} {s.ln}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>{s.cls}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 20px', fontSize: 12, color: '#64748b' }}>{s.gn || '—'}</td>
                  <td style={{ padding: '12px 20px', fontSize: 12, color: '#64748b' }}>{s.father || '—'}</td>
                  <td style={{ padding: '12px 20px', fontSize: 12, color: '#64748b' }}>{s.ph || s.fphone || '—'}</td>
                  <td style={{ padding: '12px 20px', textAlign: 'right' }}>
                    <button onClick={() => openEdit(s)} style={{ padding: '5px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#1960a3', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Edit</button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 640, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.2)', fontFamily: 'Inter,sans-serif' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#1e293b' }}>{editId ? 'Edit Student' : 'Add Student'}</div>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 20, lineHeight: 1 }}>✕</button>
            </div>
            {(() => {
              const inp = (key, placeholder, type) => (
                <input type={type || 'text'} value={form[key] || ''} onChange={e => sf(key)(e.target.value)} placeholder={placeholder}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              );
              const sel = (key, opts, onChange) => (
                <select value={form[key] || ''} onChange={onChange || (e => sf(key)(e.target.value))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' }}>
                  {opts}
                </select>
              );
              const lbl = (text) => <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 5 }}>{text}</label>;
              const sec = (text) => <div style={{ gridColumn: '1/-1', fontSize: 11, fontWeight: 800, color: '#1960a3', textTransform: 'uppercase', letterSpacing: '0.1em', paddingTop: 8, borderTop: '1px solid #f1f5f9', marginTop: 4 }}>{text}</div>;
              const fld = (label, children) => <div>{lbl(label)}{children}</div>;
              return (
                <div style={{ padding: '20px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
                  {/* Photo upload */}
                  <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 16, padding: '12px 0', borderBottom: '1px solid #f1f5f9', marginBottom: 4 }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <div style={{ width: 72, height: 72, borderRadius: 16, overflow: 'hidden', background: '#f1f5f9', border: '2px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        onClick={() => document.getElementById('tstu-photo-input').click()}>
                        {photo
                          ? <img src={photo} alt="student" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span className="material-symbols-outlined" style={{ fontSize: 28, color: '#94a3b8' }}>photo_camera</span>
                        }
                      </div>
                      <input id="tstu-photo-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {
                        const f = e.target.files[0]; if (!f) return;
                        if (f.size > 3 * 1024 * 1024) { toast('Max 3MB', 'err'); return; }
                        const r = new FileReader(); r.onload = x => setPhoto(x.target.result); r.readAsDataURL(f);
                        e.target.value = '';
                      }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>Student Photo</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>JPG/PNG · max 3MB</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={() => document.getElementById('tstu-photo-input').click()}
                          style={{ padding: '5px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#1960a3', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                          {photo ? 'Change' : 'Upload'}
                        </button>
                        {photo && <button type="button" onClick={() => setPhoto(null)}
                          style={{ padding: '5px 12px', borderRadius: 8, border: '1.5px solid #fecaca', background: '#fff', color: '#ef4444', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                          Remove
                        </button>}
                      </div>
                    </div>
                  </div>
                  {sec('Personal')}
                  {fld('First Name *', inp('fn', 'Enter first name'))}
                  {fld('Last Name', inp('ln', 'Enter last name'))}
                  {fld('Date of Birth', inp('dob', '', 'date'))}
                  {fld('Gender', sel('gn', <><option>Male</option><option>Female</option><option>Other</option></>, e => sf('gn')(e.target.value)))}
                  {fld('Blood Group', sel('bl', <><option value="">—</option>{['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(b=><option key={b}>{b}</option>)}</>, e => setForm(f => ({ ...f, bl: e.target.value, blood: e.target.value }))))}
                  {fld('Caste', sel('ca', <><option value="">—</option>{['General','OBC','SC','ST','EWS'].map(c=><option key={c}>{c}</option>)}</>))}
                  {fld('Aadhaar', inp('aa', '12-digit Aadhaar number'))}
                  {fld('Religion', inp('re', 'e.g. Hindu, Muslim'))}
                  {fld('Medical Condition', inp('mc', 'Any known condition or None'))}
                  {fld('Allergies', inp('al', 'Any allergies or None'))}

                  {sec('Academic')}
                  <div>
                    {lbl('Class *')}
                    {myClasses.length > 1
                      ? sel('cls', <><option value="">— Select class —</option>{myClasses.map(c=><option key={c}>{c}</option>)}</>)
                      : <input value={form.cls || ''} readOnly style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#64748b', background: '#f8fafc', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                    }
                  </div>
                  {fld('Roll No.', inp('ro', 'Enter roll number'))}
                  {fld('Admission No.', inp('an', 'Admission number'))}
                  {fld('Admission Date', inp('ad', '', 'date'))}
                  {fld('PEN No.', inp('pen', '11-digit PEN number'))}
                  {fld('APAAR ID', inp('apaar', '12-digit APAAR ID'))}
                  {fld('Previous School', inp('ps', 'Previous school name'))}
                  {fld('Email', inp('em', 'Student email', 'email'))}
                  {fld('Fee Status', sel('fst', <><option>Pending</option><option>Paid</option><option>Overdue</option></>))}
                  {fld('Annual Fee ₹', inp('fe', '0', 'number'))}
                  {fld('Monthly Fee ₹', inp('mf', '0', 'number'))}

                  {sec('Father')}
                  {fld("Father's Name", inp('fa', "Father's full name"))}
                  {fld('Occupation', inp('fo', "Father's occupation"))}
                  {fld("Father's Phone", inp('fp', '10-digit mobile'))}
                  {fld("Father's Aadhaar", inp('faa', '12-digit Aadhaar'))}

                  {sec('Mother')}
                  {fld("Mother's Name", inp('ma', "Mother's full name"))}
                  {fld('Occupation', inp('mo', "Mother's occupation"))}
                  {fld("Mother's Phone", inp('mp', '10-digit mobile'))}
                  {fld("Mother's Aadhaar", inp('maa', '12-digit Aadhaar'))}

                  {sec('Address')}
                  {fld('Phone / WhatsApp', inp('ph', '10-digit mobile'))}
                  {fld('City', inp('cy', 'Enter city'))}
                  <div style={{ gridColumn: '1/-1' }}>{lbl('Address')}<input value={form.addr || ''} onChange={e => sf('addr')(e.target.value)} placeholder="House no., street, locality" style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} /></div>
                  {fld('PIN Code', inp('pin', '6-digit PIN'))}

                  {sec('Parent Portal Access')}
                  <input type="text" name="tstu_nofill_u" style={{ display: 'none' }} readOnly autoComplete="off" />
                  <input type="password" name="tstu_nofill_p" style={{ display: 'none' }} readOnly autoComplete="off" />
                  {fld('Username', <input type="text" value={form.puser || ''} onChange={e => sf('puser')(e.target.value)} placeholder="Leave blank for no access" autoComplete="off" style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />)}
                  {fld('Password', <input type="password" value={form.ppass || ''} onChange={e => sf('ppass')(e.target.value)} placeholder="Minimum 6 characters" autoComplete="new-password" style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />)}
                </div>
              );
            })()}
            <div style={{ padding: '14px 24px 20px', display: 'flex', justifyContent: 'flex-end', gap: 10, borderTop: '1px solid #f1f5f9', position: 'sticky', bottom: 0, background: '#fff' }}>
              <button onClick={() => setOpen(false)} style={{ padding: '9px 20px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={saveStu} disabled={saving} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#1960a3,#3b82f6)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving…' : editId ? 'Update Student' : 'Add Student'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default TeacherStudents;
